import React, { useState, useRef, useEffect } from 'react';
import ThreeSphere from '../AMyteSphere/ThreeSphere';
import TimesheetsModal from './TimesheetsModal';

// Branding and references
export const BRAND_BLUE = '#1F5683';
export const BRAND_BLUE_DARK = '#246399';
export const BRAND_RED = '#d9534f';

export const COST_CODE_REF = [
  { code: '5000', desc: 'Steel Offloading' },
  { code: '6000', desc: 'Shakeout' },
  { code: '7000', desc: 'Erecting' },
  { code: '8000', desc: 'Bolting' },
  { code: '9000', desc: 'Welding' },
  { code: '4000', desc: 'Painting' },
  { code: '3000', desc: 'Rework' },
];

export const CREW_REF = [
  { name: 'Nicolas Randal', nickname: 'Nicky' },
  { name: 'Robert Kennedy', nickname: 'Bobby' },
  { name: 'Dion Baggles', nickname: 'Spider' },
  { name: 'Ricky Martin', nickname: 'Beck' },
];

function TimesheetsPage() {
  // State
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [parsedActivities, setParsedActivities] = useState({ timesheet: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [zoomMultiplier, setZoomMultiplier] = useState(5.5); // Default for desktop

  // Unique session for each timesheet flow
  const sessionIdRef = useRef(Date.now().toString());

  // We'll chain chunk processing in a "promise queue"
  const transcribeQueueRef = useRef(Promise.resolve());

  // iOS detection
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Dynamic zoom multiplier based on viewport width
  useEffect(() => {
    const updateZoom = () => {
      const isMobile = window.innerWidth <= 768;
      setZoomMultiplier(isMobile ? 4.5 : 5.5);
    };

    updateZoom(); // Initial call
    window.addEventListener('resize', updateZoom);
    return () => window.removeEventListener('resize', updateZoom);
  }, []);

  // Flow for the Timesheets modal
  const openModal = () => {
    sessionIdRef.current = Date.now().toString();
    setTranscript('');
    setParsedActivities({ timesheet: [] });
    setModalOpen(true);
  };

  // When closing the modal, stop recording and call the cleanup endpoint.
  const closeModal = async () => {
    setModalOpen(false);
    stopRecording();
    try {
      await fetch(`/api/timesheets/cleanup?sessionId=${sessionIdRef.current}`, {
        method: 'DELETE',
      });
    } catch (err) {
      console.error('Error cleaning up audio file:', err);
    }
  };

  // Called after each chunk is appended
  // 1) Upload chunk
  // 2) Immediately call transcribe
  // 3) Update transcript
  async function processChunk(blob, fileExtension) {
    // 1) Upload chunk
    const formData = new FormData();
    formData.append('sessionId', sessionIdRef.current);
    formData.append('audio', blob, `chunk-${Date.now()}.${fileExtension}`);
    try {
      await fetch('/api/timesheets/upload-audio', { method: 'POST', body: formData });
    } catch (err) {
      console.error('Error uploading chunk:', err);
      return;
    }

    // 2) Immediately transcribe
    try {
      const resp = await fetch(`/api/timesheets/transcribe?sessionId=${sessionIdRef.current}`);
      const data = await resp.json();
      // 3) Update transcript
      if (data.transcript && data.transcript !== transcript) {
        setTranscript(data.transcript);
      }
    } catch (err) {
      console.error('Error calling transcribe:', err);
    }
  }

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // iOS => "audio/mp4" if supported; otherwise => "audio/webm; codecs=opus"
      let recorderOptions = {};
      if (isIOS && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/mp4')) {
        recorderOptions.mimeType = 'audio/mp4';
      } else if (
        MediaRecorder.isTypeSupported &&
        MediaRecorder.isTypeSupported('audio/webm; codecs=opus')
      ) {
        recorderOptions.mimeType = 'audio/webm; codecs=opus';
      }
      // Decide file extension
      const fileExtension = isIOS && recorderOptions.mimeType === 'audio/mp4' ? 'mp4' : 'webm';

      const recorder = new MediaRecorder(stream, recorderOptions);
      setMediaRecorder(recorder);
      setRecording(true);

      // For each chunk, queue it in a promise chain
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          // Add chunk to the chain
          transcribeQueueRef.current = transcribeQueueRef.current.then(() =>
            processChunk(e.data, fileExtension)
          );
        }
      };

      // timeslice = short on iOS, else 4000
      const timeslice = isIOS ? 1000 : 4000;
      recorder.start(timeslice);
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
    setRecording(false);
  };

  // Analyze transcript => GPT
  const analyzeTranscript = async () => {
    setIsAnalyzing(true);
    try {
      const resp = await fetch('/api/timesheets/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript }),
      });
      const data = await resp.json();
      if (data.timesheetData) {
        setParsedActivities(data.timesheetData);
      } else if (data.rawResponse) {
        console.warn('GPT returned unstructured data:', data.rawResponse);
      }
    } catch (err) {
      console.error('Analyze failed:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Save timesheet to DB
  const saveTimesheetToDb = async () => {
    try {
      const resp = await fetch('/api/timesheets/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rawTranscription: transcript,
          activities: parsedActivities,
          sessionId: sessionIdRef.current,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        alert(data.error || 'Failed to save timesheet.');
        return null;
      }
      return data.timesheetId;
    } catch (err) {
      console.error('Save failed:', err);
      return null;
    }
  };

  // Save & Download PDF
  const handleSaveAndDownloadPdf = async () => {
    const id = await saveTimesheetToDb();
    if (!id) return;
    const pdfUrl = `/api/timesheets/pdf?timesheetId=${id}`;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', `Timesheet_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    closeModal();
  };

  return (
    <div className="bg-black min-h-screen text-white flex flex-col items-center">
      <div className="text-center pt-1">
        <img
          src="/LogoFile.png"
          alt="Myte Logo"
          className="mx-auto w-[300px] max-w-[50vw] mb-[-10px]"
        />
      </div>

      <div className="relative w-full max-w-[1000px] aspect-square mx-auto flex justify-center items-center">
        <ThreeSphere noAutoTransition zoomMultiplier={zoomMultiplier} />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center pointer-events-none">
          <h2 className="text-[clamp(16px,3vw,24px)] font-bold pulsate">Speak Your Timesheets</h2>
          <h3 className="text-[clamp(16px,3vw,24px)] mt-2 pulsate-slow">Into Existence</h3>
          <div className="mt-4 pointer-events-auto">
            <button
              onClick={openModal}
              className="bg-gradient-to-r from-[#1F5683] to-[#246399] text-white px-6 py-3 rounded-md text-base cursor-pointer transition-transform duration-200 active:scale-95"
            >
              New Timesheet
            </button>
          </div>
        </div>
      </div>

      <TimesheetsModal
        modalOpen={modalOpen}
        closeModal={closeModal}
        recording={recording}
        startRecording={startRecording}
        stopRecording={stopRecording}
        transcript={transcript}
        setTranscript={setTranscript}
        isAnalyzing={isAnalyzing}
        analyzeTranscript={analyzeTranscript}
        handleSaveAndDownloadPdf={handleSaveAndDownloadPdf}
        parsedActivities={parsedActivities}
        setParsedActivities={setParsedActivities}
      />
    </div>
  );
}

export default TimesheetsPage;